import {
  Button,
  MultiSelectDropDown,
  SelectDropDown,
} from '@/components/Common';
import { Modal } from '@/components/Layout';
import { useGetDiscplines } from '@/api/hooks';
import { FieldError } from 'react-hook-form';
import { Options } from '@/components/Common/Selects/Select.types';
import { useGetLessons } from '@/api/hooks/useLesson';
import type { GroupedLessonsOutput } from '@/api/gql/graphql';
import {
  useConfigDisciplineWithLesson,
  DisciplineWithLessonsInput,
} from '@/hooks/useConfigExam';
import { useDisciplineWithLessonStore } from '@/store/ExamStore/DisciplineWithLessonStore';
import { useDisciplineLessonPrefill } from './useDisciplineLessonPrefill';

interface AddDisciplineWithLessonProps {
  isOpen: boolean;
  onClose: () => void;
  editingDiscipline?: {
    index: number;
    data: DisciplineWithLessonsInput;
  } | null;
  onUpdate?: (updatedDiscipline: DisciplineWithLessonsInput) => void;
}

export const AddDisciplineWithLesson = ({
  isOpen,
  onClose,
  editingDiscipline,
  onUpdate,
}: AddDisciplineWithLessonProps) => {
  const { handleSubmit, errors, setValue, reset } =
    useConfigDisciplineWithLesson();
  const { data: disciplines } = useGetDiscplines();
  const { data: lessonsData } = useGetLessons({
    disciplines: undefined, // on laisse undefined, la sélection se fait dans le hook
  });

  const {
    selectedDiscipline,
    setSelectedDiscipline,
    selectedLessons,
    setSelectedLessons,
  } = useDisciplineLessonPrefill({
    editingDiscipline,
    disciplines,
    lessonsData,
    setValue,
  });

  const handleChangeDiscipline = (value: Options) => {
    setSelectedDiscipline(value);
    setSelectedLessons([]);
    setValue('discipline', {
      _id: value._id as string,
      name: value.name as string,
    });
    setValue('lessons', []);
  };

  const handleChangeLessons = (values: Options[]) => {
    setSelectedLessons(values);
    setValue(
      'lessons',
      values.map((lesson) => ({
        _id: lesson._id as string,
        name: lesson.name as string,
      })),
    );
  };

  const onSubmit = (data: DisciplineWithLessonsInput) => {
    const addDisciplineWithLesson =
      useDisciplineWithLessonStore.getState().addDisciplineWithLesson;
    if (editingDiscipline && onUpdate) {
      onUpdate(data);
    } else {
      addDisciplineWithLesson(data);
    }
    reset();
    setSelectedDiscipline(null);
    setSelectedLessons([]);
    setValue('discipline', '');
    setValue('lessons', []);
    onClose();
  };

  const lessons = selectedDiscipline
    ? lessonsData?.find(
        (item: GroupedLessonsOutput) =>
          item.discipline._id === selectedDiscipline._id,
      )?.lessons || []
    : [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      modalStyle="bg-bgModal"
      headerStyle="bg-bgModal h-[36px]"
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-[653px] flex flex-col items-center"
      >
        <div className="w-[315px]">
          <div className="flex flex-col justify-center items-center mb-7">
            <span className="text-xl text-primary font-bold">
              Ajoutez une matière
            </span>
            <span className="text-xs text-[#9FA9B2] font-normal">
              et les leçons associées
            </span>
          </div>
          <SelectDropDown
            selectDropDownStyle={{
              borderRadius: '0.375rem',
              color: '#fff',
              padding: ' 0.5rem',
            }}
            placeholder="Discpline"
            name="discipline"
            setValue={setValue}
            initialSelectedOptions={selectedDiscipline}
            helpText={(errors.discipline as FieldError)?.message}
            errorStyle="text-red-500"
            options={disciplines || []}
            handleChange={handleChangeDiscipline}
          />
          <div>
            <MultiSelectDropDown
              placeholder={
                selectedDiscipline
                  ? 'Leçons'
                  : "Sélectionnez d'abord une discipline"
              }
              name="lessons"
              setValue={setValue}
              initialSelectedOptions={selectedLessons}
              helpText={(errors.lessons as FieldError)?.message}
              errorStyle="text-red-500"
              options={lessons}
              selectDropDownStyle={{
                fontSize: '12px',
              }}
              handleChange={handleChangeLessons}
              className="w-[415px]"
            />
          </div>
        </div>
        <Button type="submit" title="Ajouter" className="w-[315px] mt-10" />
      </form>
    </Modal>
  );
};
